import { inject } from 'mobx-react'
import React from 'react'
import { IStores } from 'src/Stores'
import WorkflowInbox from './WorkflowInbox'
import { Button, CircularProgress, Grid } from '@mui/material'
import { useParams } from 'react-router-dom'
import { InsertDriveFile } from '@mui/icons-material'
import {
  getDefaultDocumentInboxItem,
  getDocumentInboxItem,
  processToWorkflow,
} from 'src/services/WorkflowService'
import { IncomingDocumentSummary } from 'src/viewModels/IncomingDocumentSummary'
import { DocumentInboxItem } from 'src/viewModels/workflow/DocumentInboxItem'
import { InboxItemType } from 'src/utils/InboxItemType'
import createDocumentUrl from 'src/utils/createDocumentUrl'

interface IDocumentInboxProps {
  documentName?: string
  documentId?: string
  inboxId?: string
  rerouteToPath?: (path: string) => void
}

class DocumentInbox extends React.Component<IDocumentInboxProps> {
  public state = {
    docData: '',
    headerInfo: undefined as unknown as DocumentInboxItem,
    isLoading: true,
  }

  public async componentDidMount(): Promise<void> {
    if (this.props.documentName) {
      var summary = new IncomingDocumentSummary()
      summary.name = this.props.documentName

      try {
        const response = await processToWorkflow(summary)
        if (response?.associatedItemId && response?.inboxId) {
          const path = `/workflow/document/${response.associatedItemId}/inbox/${response.inboxId}`
          this.props.rerouteToPath!(path)
        } else {
          this.props.rerouteToPath!(`/workflow-inboxes`)
        }
      } catch {
        this.props.rerouteToPath!(`/workflow-inboxes`)
      }
    } else {
      try {
        const response = this.props.inboxId
          ? await getDocumentInboxItem(this.props.documentId!, this.props.inboxId)
          : await getDefaultDocumentInboxItem(this.props.documentId!)

        // Use URL if available, otherwise create data URL from content
        let doc = ''
        if (response.associatedStagedDocument.url) {
          doc = response.associatedStagedDocument.url
        } else {
          const content = Array.isArray(response.associatedStagedDocument.content)
            ? response.associatedStagedDocument.content.join('')
            : (response.associatedStagedDocument.content ?? '')
          doc = createDocumentUrl(
            content,
            response.associatedStagedDocument.mime ?? 'application/octet-stream'
          )
        }

        this.setState({
          docData: doc,
          headerInfo: response,
          isLoading: false,
        })
      } catch {
        this.props.rerouteToPath!(`/workflow-inboxes`)
      }
    }
  }

  public async componentDidUpdate(prevProps: IDocumentInboxProps): Promise<void> {
    if (
      (!prevProps.documentId && this.props.documentId) ||
      (!prevProps.inboxId && this.props.inboxId) ||
      prevProps.documentId !== this.props.documentId ||
      prevProps.inboxId !== this.props.inboxId
    ) {
      if (!this.props.documentName && this.props.documentId && this.props.inboxId) {
        this.setState({ isLoading: true })
        try {
          const response = await getDocumentInboxItem(
            this.props.documentId,
            this.props.inboxId
          )

          // Use URL if available, otherwise create data URL from content
          let doc = ''
          if (response.associatedStagedDocument.url) {
            doc = response.associatedStagedDocument.url
          } else {
            const content = Array.isArray(response.associatedStagedDocument.content)
              ? response.associatedStagedDocument.content.join('')
              : (response.associatedStagedDocument.content ?? '')
            doc = createDocumentUrl(
              content,
              response.associatedStagedDocument.mime ?? 'application/octet-stream'
            )
          }

          this.setState({
            docData: doc,
            headerInfo: response,
            isLoading: false,
          })
        } catch {
          this.props.rerouteToPath!(`/workflow-inboxes`)
        }
      }
    }
  }

  public renderDoc() {
    const supplementalDocumentUri =
      this.state.headerInfo?.supplementalDocumentOriginalDocumentUri
    const claimId = this.state.headerInfo?.claimId

    return (
      <Grid
        container
        spacing={2}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'flex-end',
          flexDirection: 'column',
          paddingTop: '8px',
        }}
      >
        <Grid item>
          {supplementalDocumentUri && (
            <Button
              variant="contained"
              color="primary"
              aria-label="open-supplemental-document"
              onClick={() =>
                window.open(supplementalDocumentUri, 'new', 'width=1200,height=800')
              }
            >
              Open Supplemental Document
            </Button>
          )}
          {claimId && (
            <Button
              variant="contained"
              color="primary"
              aria-label="open-claim-default-inbox"
              onClick={() =>
                window.open(`/workflow/claim/${claimId}/default-inbox`, '_blank')
              }
              style={{ marginLeft: supplementalDocumentUri ? '8px' : 0 }}
            >
              Open Claim
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            aria-label="view-in-new-window"
            onClick={() =>
              window.open(this.state.docData, 'new', 'width=1200,height=800')
            }
            disabled={!this.state.docData}
            style={{ marginLeft: supplementalDocumentUri || claimId ? '8px' : 0 }}
          >
            View in New Window
          </Button>
        </Grid>
        {this.state.docData && (
          <iframe
            src={this.state.docData}
            height="600px"
            width="100%"
            style={{ border: 'none' }}
          />
        )}
      </Grid>
    )
  }

  public render() {
    return (
      //Spinner
      <>
        {this.state.isLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '2',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <WorkflowInbox
          inboxType={InboxItemType.Document}
          item={this.renderDoc()}
          headerInfo={this.state.headerInfo}
          icon={<InsertDriveFile />}
        />
      </>
    )
  }
}

const InjectedDocumentInbox = inject<
  IStores,
  IDocumentInboxProps,
  Partial<IDocumentInboxProps>,
  any
>((stores: IStores) => ({
  rerouteToPath: stores.global.rerouteToPath,
}))(DocumentInbox)

// Wrapper component to extract route params and pass them as props
const DocumentInboxWrapper: React.FC = () => {
  const params = useParams<{
    documentName?: string
    documentId?: string
    inboxId?: string
  }>()

  const key = params.documentName || `${params.documentId}-${params.inboxId}`

  return (
    <InjectedDocumentInbox
      key={key}
      documentName={params.documentName}
      documentId={params.documentId}
      inboxId={params.inboxId}
    />
  )
}

export default DocumentInboxWrapper
